import React, { useMemo, useState, useEffect, useRef } from "react";
import gsap from "gsap";
import "./EnergyEstimator.css";

const PROPERTY_TYPES = ["Independent House", "Apartment / Flat", "Commercial Unit"];

// Simple placeholder estimate logic — replace with real calculation later.
function estimate(bill, propertyType) {
  const monthlyUnits = Math.max(bill, 0) / 8; // ~₹8/unit placeholder
  const dailyUnits = monthlyUnits / 30;
  let capacityKw = dailyUnits / 4; // ~4 sun-hours/day placeholder
  if (propertyType === "Commercial Unit") capacityKw *= 1.15;
  capacityKw = Math.max(capacityKw, 1);

  const yearlyGeneration = capacityKw * 4 * 365;
  const annualSavings = yearlyGeneration * 8;

  return {
    capacity: Math.round(capacityKw * 10) / 10,
    yearly: Math.round(yearlyGeneration),
    savings: Math.round(annualSavings),
  };
}

function AnimatedNumber({ value, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const proxy = useRef({ val: 0 });

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      if (ref.current) ref.current.textContent = `${prefix}${value.toLocaleString("en-IN")}${suffix}`;
      return;
    }
    gsap.to(proxy.current, {
      val: value,
      duration: 0.9,
      ease: "power2.out",
      onUpdate: () => {
        if (ref.current) {
          ref.current.textContent = `${prefix}${Math.round(
            proxy.current.val
          ).toLocaleString("en-IN")}${suffix}`;
        }
      },
    });
  }, [value, prefix, suffix]);

  return <span ref={ref}>{prefix}0{suffix}</span>;
}

export default function EnergyEstimator() {
  const [bill, setBill] = useState(3000);
  const [propertyType, setPropertyType] = useState(PROPERTY_TYPES[0]);
  const [location, setLocation] = useState("Coimbatore");

  const result = useMemo(() => estimate(bill, propertyType), [bill, propertyType]);

  return (
    <section className="estimator section">
      <div className="container">
        <span className="eyebrow">Free Estimate</span>
        <h2 className="estimator__title">
          HOW MUCH ENERGY
          <br />
          <span className="glow-text">COULD YOUR ROOF CREATE?</span>
        </h2>

        <div className="estimator__grid">
          <div className="estimator__inputs">
            <label className="estimator__field">
              <span>Monthly electricity bill (₹)</span>
              <input
                type="range"
                min="500"
                max="20000"
                step="100"
                value={bill}
                onChange={(e) => setBill(Number(e.target.value))}
              />
              <strong>₹{bill.toLocaleString("en-IN")}</strong>
            </label>

            <label className="estimator__field">
              <span>Property type</span>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
              >
                {PROPERTY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>

            <label className="estimator__field">
              <span>Location</span>
              <select value={location} onChange={(e) => setLocation(e.target.value)}>
                <option>Coimbatore</option>
                <option>Salem</option>
                <option>Other Tamil Nadu district</option>
              </select>
            </label>

            <p className="estimator__disclaimer">
              These are approximate, placeholder estimates for illustration
              only — final system sizing follows an on-site assessment.
            </p>
          </div>

          <div className="estimator__results">
            <div className="estimator__result">
              <AnimatedNumber value={result.capacity} suffix=" kW" />
              <span>estimated system size</span>
            </div>
            <div className="estimator__result">
              <AnimatedNumber value={result.yearly} suffix=" kWh" />
              <span>estimated yearly generation</span>
            </div>
            <div className="estimator__result estimator__result--highlight">
              <AnimatedNumber prefix="₹ " value={result.savings} />
              <span>potential annual savings</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
